import React from 'react';
import { motion } from 'framer-motion';

const features = [
  {
    title: 'Custom Booking Slots',
    description: "Create categorised, configurable booking slots tailored to your site's specific operational needs.",
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
      </svg>
    ),
  },
  {
    title: 'Real-Time Availability',
    description: 'Track slot availability live to prevent bottlenecks, overbooking, and scheduling conflicts.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Integrated Scheduling',
    description: 'Seamlessly syncs with Clutch Logistics for a fully unified transport and scheduling operation.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    ),
  },
  {
    title: 'Communication Tools',
    description: 'Keep drivers, site managers, and dispatch teams aligned with automated booking notifications.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
  },
  {
    title: 'Workflow Optimisation',
    description: 'Reduce delays and minimise idle time with intelligent scheduling tools built for high-volume sites.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    title: 'Multi-Site Support',
    description: 'Manage booking operations across multiple receival sites and locations from a single account.',
    icon: (
      <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
      </svg>
    ),
  },
];

const Bookings = () => {
  return (
    <>
      {/* Hero */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />

        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Left — text */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                Smart Scheduling
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-primary mt-2 mb-6 leading-tight">
                Clutch<br />
                <span className="text-gradient-blue">Booking</span>
              </h1>
              <p className="text-secondary text-lg leading-relaxed mb-8">
                A robust and versatile platform designed to streamline booking management for time-sensitive
                industries and site operations. Whether managing drop-offs, pick-ups, or appointment slots —
                Clutch Booking gives you the tools for efficient coordination and seamless scheduling.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://clutch-support.atlassian.net/servicedesk/customer/portal/7/group/13/create/44"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Request a Demo
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                  </svg>
                </a>
                <a
                  href="https://bookings.clutchit.com.au/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                >
                  View Platform
                </a>
              </div>
            </motion.div>

            {/* Right — image */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="./images/booking_img.png"
                  alt="Clutch Booking Platform"
                  className="w-full h-auto object-cover object-left"
                />
              </div>
              </motion.div>
              {/* Launch button */}
              <button
                onClick={() => window.open('https://bookings.clutchit.com.au/', '_blank')}
                className="absolute right-4 bottom-4 w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center hover:bg-accent-hover hover:shadow-glow-sm transition-all duration-200 cursor-pointer"
                aria-label="Open Clutch Booking"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </button>
              {/* Product label */}
              <div className="mt-4">
                <p className="text-primary font-semibold">Clutch Booking</p>
                <p className="text-secondary text-sm">Scheduling Platform</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-surface-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />

        <div className="section-container relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Scheduling Tools Built for<br />
              <span className="text-gradient-blue">High-Volume Operations</span>
            </h2>
            <p className="text-secondary text-lg max-w-2xl mx-auto">
              Every feature is designed around the real demands of agricultural site management and logistics coordination.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(({ title, description, icon }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="rounded-2xl border border-border-subtle bg-white p-6 shadow-card hover:border-accent hover:shadow-glow-sm transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors duration-300">
                  {icon}
                </div>
                <h3 className="text-primary font-semibold mb-2">{title}</h3>
                <p className="text-secondary text-sm leading-relaxed">{description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail + screenshots */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />

        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* Screenshots */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-3 gap-3"
            >
              {['/images/logo_booking.png', '/images/booking_cal.png', '/images/booking_prc.png'].map((src, i) => (
                <div key={i} className="rounded-xl border border-border-subtle overflow-hidden shadow-card aspect-square bg-surface-1">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </motion.div>

            {/* Detail text + CTA */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="text-3xl font-bold text-primary mb-4">
                Scheduling Meets<br />
                <span className="text-gradient-blue">Logistics Intelligence</span>
              </h2>
              <p className="text-secondary leading-relaxed mb-4">
                The platform empowers site managers to create customised, categorised booking slots, track
                availability in real-time, and optimise workflows to prevent bottlenecks and minimise delays.
              </p>
              <p className="text-secondary leading-relaxed mb-8">
                When integrated with the Clutch Logistics platform, Clutch Booking delivers a comprehensive
                solution by aligning scheduling with transport and asset management — ensuring smooth operations
                and improved communication across all facets of your business.
              </p>
              <a
                href="https://clutch-support.atlassian.net/servicedesk/customer/portal/7/group/13/create/44"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Request a Demo
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Bookings;
